import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Send, Mail, MousePointerClick, ArrowUpRight, Clock } from "lucide-react";

function StatCard({ label, value, icon: Icon, tone, note, testid }) {
  const tones = {
    primary: "bg-primary/10 text-primary border-primary/20",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <Card className="p-6 rounded-2xl border-slate-200 card-hover" data-testid={testid}>
      <div className={`inline-flex h-10 w-10 rounded-xl items-center justify-center border ${tones[tone] || tones.slate} mb-4`}>
        <Icon className="h-5 w-5" strokeWidth={2.2} />
      </div>
      <div className="text-4xl font-display font-extrabold text-slate-900 tracking-tight" data-testid={`${testid}-value`}>{value}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
      {note && <div className="text-xs text-slate-400 mt-2">{note}</div>}
    </Card>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Last 30 days at a glance.</p>
        </div>
        <Link to="/send"><Button className="rounded-full h-12 px-6 bg-primary hover:bg-primary/90 text-base w-full md:w-auto" data-testid="dashboard-request-btn">
          <Send className="mr-2 h-4 w-4" /> Request a review
        </Button></Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Requests sent" value={stats?.sent_30d ?? 0} icon={Send} tone="slate" testid="stat-sent" />
        <StatCard label="Emails opened" value={stats?.opened_30d ?? 0} icon={Mail} tone="slate" testid="stat-opened" note="Approximate — Apple Mail inflates opens." />
        <StatCard label="Review link clicked" value={stats?.clicked_30d ?? 0} icon={MousePointerClick} tone="primary" testid="stat-clicked" />
      </div>

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">Recent activity</h2>
          <Link to="/customers" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="dashboard-all-customers">
            All customers <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {stats?.recent?.length ? (
          <ul className="divide-y divide-slate-100" data-testid="dashboard-recent-list">
            {stats.recent.map((r, i) => {
              const action = r.clicked_at ? "clicked your review link" : r.opened_at ? "opened your email" : r.status === "failed" ? "email failed" : "email sent";
              const when = r.clicked_at || r.opened_at || r.created_at;
              return (
                <li key={r.id} className="px-6 py-4 flex items-center gap-4 fade-up" style={{ animationDelay: `${i * 30}ms` }}>
                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary grid place-items-center font-bold">{(r.customer_name || "?")[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-900 font-medium truncate">{r.customer_name}</div>
                    <div className="text-sm text-slate-500 truncate">{action}</div>
                  </div>
                  <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{timeAgo(when)}</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="p-10 text-center text-slate-500" data-testid="dashboard-empty">
            <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-slate-100 grid place-items-center"><Send className="h-6 w-6 text-slate-400" /></div>
            <p className="font-medium text-slate-700">No activity yet</p>
            <p className="text-sm">Add a customer or import a CSV to send your first review request.</p>
            <div className="mt-4 flex justify-center gap-2">
              <Link to="/customers"><Button variant="outline" className="rounded-full" data-testid="dashboard-add-customer">Add customer</Button></Link>
              <Link to="/send"><Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="dashboard-send-request">Send a request</Button></Link>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
